import GoalReportsRow from '@features/reports/components/goals/management/GoalReportRow'
import { Heading } from '@lmc/ui-kit'
import { useAbilityAccess } from 'components/AccessControl'
import { getRouteWithParams } from 'components/routes/utils'
import useFeatureFlags from 'hooks/useFeatureFlags'
import { useNavigate } from 'react-router-dom'
import styled from 'styled-components'
import { commonPatterns } from 'utils/enhancedTestIds'
import GoalItem, { type GoalItemBase } from './GoalItem'
import GoldCoinIcon from './GoldCoinIcon.svg'

export type GoalsListItem = Omit<GoalItemBase, 'disabled' | 'onUnlink'> & {
  id: string
  key: string
}

type GoalsListProps = {
  title?: string
  items: Omit<GoalsListItem, 'disabled' | 'onUnlink'>[]
  className?: string
  /**
   * @default true
   */
  disabled?: boolean
  /**
   * Only when disabled is false
   */
  onUnlink?: (id: string) => void
  residentID: string | undefined
  hasBorder?: boolean
  testId?: string
}

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`

const ItemsWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`

const GoalsList = (props: GoalsListProps) => {
  const {
    title,
    items,
    className,
    disabled = true,
    onUnlink,
    residentID,
    hasBorder,
    testId
  } = props

  const navigate = useNavigate()

  const hasRolePermission = useAbilityAccess([
    {
      i: 'view',
      this: 'Goal'
    }
  ])

  const onItemClick = (goalID: string) => {
    if (!disabled || !hasRolePermission) {
      return
    }

    const route = getRouteWithParams('/residents/:id/goals/:goalID', {
      goalID,
      id: residentID
    })

    navigate(route)
  }

  const flags = useFeatureFlags({
    flagNames: ['outcomes_and_goals_v2']
  })
  const outcomesAndGoalsV2 = flags?.data?.outcomes_and_goals_v2?.enabled

  if (flags?.isLoading) {
    return null
  }

  return (
    <Wrapper className={className} data-testid={testId}>
      {title && <Heading level={4}>{title}</Heading>}
      <ItemsWrapper>
        {items.map((item, index) =>
          outcomesAndGoalsV2 ? (
            <GoalReportsRow
              key={item.id}
              goal={{
                id: item.id,
                title: item.title,
                progress: item.progress ?? 0,
                lastReviewedDate: item.editedOn || item.createdOn,
                reviewedBy: item.editedBy || item.createdBy,
                dueDate: item.nextReviewDate ?? '',
                status: item.status
              }}
              onClick={() => onItemClick(item.id)}
              lightVariant
              testId={testId ? commonPatterns.row(testId, index) : undefined}
            />
          ) : (
            <GoalItem
              {...item}
              cursor={disabled ? 'pointer' : 'default'}
              disabled={disabled}
              onUnlink={onUnlink}
              onClick={() => onItemClick(item.id)}
              iconContent={<img alt="Gold coin Icon" src={GoldCoinIcon} />}
              hasBorder={hasBorder}
              testId={testId ? commonPatterns.row(testId, index) : undefined}
            />
          )
        )}
      </ItemsWrapper>
    </Wrapper>
  )
}

export default GoalsList
